/**
 * @fileoverview PhotoButton: a round see-through button on a picture, and PhotoMark, its look without the press.
 *
 * Photo apps put a picture's tools on the picture, as round dark glass: dark
 * with a faint white edge so it reads on any photograph, light or dark. The
 * plant card's photo tools and its arrows (PlantCardPhoto) are the pattern,
 * and every round button over a picture in the app is this one: the card's
 * tools, the arrows on a photo strip, the share screen's preview arrows and
 * full-size button, a removing × on a tray photo.
 *
 * Four sizes: xs (24px, a mark's size), sm (28px to see and 44px to hit, set
 * apart so no tap belongs to two), md (40px, the arrows, which hit
 * themselves) and pill (2rem tall, with words beside its icon). Two tones:
 * dark glass (the default) and white glass (a restore button on an archived
 * bed's tile, the pencil hint on a picture); both read on any photograph, in
 * every theme.
 *
 * A press on one is its own: it does not start the strip's drag or open the
 * picture under it, so it stops the pointer-down and the click reaching them
 * (stopPress, on by default).
 *
 * Where: the plant and bed cards (PlantCardPhoto, PlantCard, LocationCard,
 * LocationTile), the edit and add photo screens (Replace, Download, Choose
 * another), the share screen's preview, the public garden pages' photo strips
 * and the photo tray's and packet review's thumbnails.
 * API: none.
 * Words: none; the caller gives its label.
 *
 * @module PhotoButton
 */

import * as React from 'react';
import type { VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { photoButtonVariants } from '@/shared/styles/variants';

/** What a photo mark is given: its size and tone, and what it holds. */
export interface PhotoMarkProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof photoButtonVariants> {}

/**
 * A round glass mark on a picture that does nothing when pressed: a hint, or a
 * badge (the crossed-out eye on a photo hidden from the public pages). It lets
 * the press through to the picture.
 *
 * @param props - PhotoMarkProps.
 */
export const PhotoMark = React.forwardRef<HTMLSpanElement, PhotoMarkProps>(
  ({ size, tone, className, ...rest }, ref) => (
    <span {...rest} ref={ref} className={cn(photoButtonVariants({ size, tone }), 'pointer-events-none', className)} />
  ),
);
PhotoMark.displayName = 'PhotoMark';

/** What a photo button is given: its name, its size and tone, and whether its press stays its own. */
export interface PhotoButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'>,
    VariantProps<typeof photoButtonVariants> {
  /** Its name, read out and shown on pointing. */
  label: string;
  /** Keeps its press from reaching what is under it (a strip's drag, the picture's own tap). On by default. */
  stopPress?: boolean;
}

/**
 * One tool or arrow on a picture: a round see-through button with its icon.
 *
 * Other button props pass through, so a menu can use it as its trigger.
 *
 * @param props - PhotoButtonProps.
 */
export const PhotoButton = React.forwardRef<HTMLButtonElement, PhotoButtonProps>(
  ({ label, size, tone, stopPress = true, className, onPointerDown, onClick, children, ...rest }, ref) => (
    <button
      {...rest}
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      onPointerDown={(event) => {
        if (stopPress) event.stopPropagation();
        onPointerDown?.(event);
      }}
      onClick={(event) => {
        if (stopPress) event.stopPropagation();
        onClick?.(event);
      }}
      className={cn(
        photoButtonVariants({ size, tone }),
        'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
        // Hover: a little darker glass, or a solid light one.
        tone === 'light' ? 'hover:bg-white' : 'hover:bg-black/60',
        // The small size is 28px to see and 44px to hit.
        (size ?? 'sm') === 'sm' && "before:absolute before:-inset-2 before:rounded-full before:content-['']",
        (size ?? 'sm') === 'xs' && "before:absolute before:-inset-1.5 before:rounded-full before:content-['']",
        'disabled:opacity-50',
        className,
      )}
    >
      {children}
    </button>
  ),
);
PhotoButton.displayName = 'PhotoButton';
